import { IconeHistorique } from '@/components/historique/icone-historique';
import { RondChargement } from '@/components/noyaux/noyau-en-cours';
import { PastilleNoyau } from '@/components/noyaux/pastille-noyau';
import { IconeTrait } from '@/components/ui/icone-trait';
import { Checkbox, Icon } from '@pms-digital/design-system';
import { type EtatNoyauTexte, NOYAUX, type NoyauCode } from '@rosetta/shared';
import { Link } from '@tanstack/react-router';
import { useId } from 'react';

interface LigneNoyauAjoutProps {
  noyau: NoyauCode;
  etat: EtatNoyauTexte;
  coche: boolean;
  onBasculer: () => void;
  /** The translation whose result "voir le résultat" opens, on this noyau. */
  idResultat: string;
}

/** A noyau that cannot be added: translated already, or being translated. */
export const estPris = (etat: EtatNoyauTexte): boolean => etat === 'traduit' || etat === 'en_cours';

/**
 * One noyau in the choice of the noyaux to add (#11, N1 and N2): a checkbox (the ARIA pattern of
 * the noyau buttons and of the filter menu) with its dot, its code and its long name, then what
 * became of it for this text.
 *  - free: an empty box;
 *  - "interrompue" or "n'a pas abouti": an empty box too, it can be tried again, and the mention;
 *  - "déjà traduit": `check_circle` (the finished steps of W1), `aria-disabled`, described by its
 *    mention, and "voir le résultat" on that noyau;
 *  - "en cours": a spinner, `aria-disabled`, described by its mention.
 * A box that cannot be checked keeps the focus (`aria-disabled`, never `disabled`): a screen reader
 * user reaches it and hears why.
 */
export function LigneNoyauAjout({
  noyau,
  etat,
  coche,
  onBasculer,
  idResultat,
}: LigneNoyauAjoutProps) {
  const mentionId = useId();
  const pris = estPris(etat);
  const { famille, variante } = NOYAUX[noyau];

  function mention() {
    switch (etat) {
      case 'traduit':
        return 'déjà traduit';
      case 'en_cours':
        return 'en cours';
      case 'interrompu':
        return (
          <>
            <IconeTrait name="stop_circle" size={16} />
            interrompue
          </>
        );
      case 'echoue':
        return (
          <>
            <span className="inline-flex shrink-0 text-rouge-deep">
              <Icon name="error" size={16} />
            </span>
            n'a pas abouti
          </>
        );
      case 'libre':
        return null;
    }
  }

  /** What stands in place of the box of a noyau that cannot be checked: done, or turning. */
  function boite() {
    if (etat === 'traduit')
      return <IconeTrait name="check_circle" size={22} className="text-bleu-nuit" />;
    if (etat === 'en_cours') {
      return (
        <span className="inline-flex w-[22px] justify-center">
          <RondChargement />
        </span>
      );
    }
    return null;
  }

  const texteMention = mention();
  const contenu = (
    <span className="inline-flex min-w-0 items-center gap-3">
      <PastilleNoyau noyau={noyau} />
      <span className="w-[38px] font-bold text-small leading-none tracking-[1px]">{noyau}</span>
      <span className="text-body leading-[1.3]">
        <span className="font-semibold">{famille.charAt(0).toUpperCase() + famille.slice(1)}</span>{' '}
        <span className="font-light">{variante}</span>
      </span>
    </span>
  );
  return (
    <li className="flex min-h-[54px] items-center gap-3 border-gray-100 border-b">
      {pris ? (
        <button
          type="button"
          // biome-ignore lint/a11y/useSemanticElements: a box that cannot be checked keeps the focus (`aria-disabled`), which a disabled `Checkbox` of the package would take away
          role="checkbox"
          aria-checked={false}
          aria-disabled
          aria-describedby={texteMention === null ? undefined : mentionId}
          className="-ml-2 inline-flex h-11 min-w-0 grow cursor-not-allowed items-center gap-3 rounded-md px-2 text-left text-gray-700"
        >
          {boite()}
          {contenu}
        </button>
      ) : (
        <div className="min-w-0 grow py-2">
          <Checkbox
            checked={coche}
            onCheckedChange={onBasculer}
            aria-describedby={texteMention === null ? undefined : mentionId}
            label={contenu}
          />
        </div>
      )}
      {texteMention === null ? null : (
        <span
          id={mentionId}
          className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-caption text-gray-700 leading-[1.4]"
        >
          {texteMention}
        </span>
      )}
      {etat === 'traduit' ? (
        <Link
          to="/resultat/$id"
          params={{ id: idResultat }}
          search={{ noyau }}
          aria-label={`voir le résultat ${noyau}`}
          className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-bleu-nuit text-small no-underline hover:underline"
        >
          voir le résultat
          <IconeHistorique name="arrow_forward" size={18} />
        </Link>
      ) : null}
    </li>
  );
}
